import { facebookAdAccountService } from '@/services/facebook-ad-account.service';
import { LeadPageSubscriptionCard } from '@/components/facebook/LeadPageSubscriptionCard';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { LoadErrorState } from '@/components/ListStates';
import { facebookLeadFormsService, LeadGenFormSummary } from '@/services/facebook-lead-forms.service';
import { ClipboardList, Plus, RefreshCw, Archive, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';

function relativeTime(ts?: string) {
  if (!ts) return '—';
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return ts; }
}

/** "DRAFT" → "Draft". */
function statusLabel(status?: string) {
  if (!status) return 'No status';
  return status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ');
}

export default function LeadFormsList() {
  const nav = useNavigate();
  const loadVersion = useRef(0);
  const [pageId, setPageId] = useState('');
  const [pages, setPages] = useState<string[]>([]);
  const [forms, setForms] = useState<LeadGenFormSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [archivingId, setArchivingId] = useState<string | null>(null);

  const load = () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setLoadError(false);
    facebookLeadFormsService.list(pageId || undefined)
      .then(value => { if (version === loadVersion.current) setForms(value); })
      .catch(() => { if (version === loadVersion.current) setLoadError(true); })
      .finally(() => { if (version === loadVersion.current) setLoading(false); });
  };

  useEffect(() => {
    let active = true;
    facebookAdAccountService.get().then(account => {
      if (!active) return;
      const ids = account.pageIds?.length ? account.pageIds : account.pageId ? [account.pageId] : [];
      setPages(ids); setPageId(account.pageId && ids.includes(account.pageId) ? account.pageId : ids[0] || '');
    }).catch(() => undefined);
    return () => { active = false; };
  }, []);
  useEffect(load, [pageId]);

  const archive = async (form: LeadGenFormSummary) => {
    if (!window.confirm(`Archive "${form.name}"?\n\nMeta doesn't allow un-archiving via API. Existing leads stay accessible, but the form stops accepting new submissions.`)) {
      return;
    }
    setArchivingId(form.id);
    try {
      await facebookLeadFormsService.remove(form.id);
      toast.success('Form archived');
      load();
    } catch (e: any) {
      if (!e?.response) toast.error(e?.message ?? 'Archive failed');
    } finally {
      setArchivingId(null);
    }
  };

  const active = forms.filter(f => f.status === 'ACTIVE');
  const archived = forms.filter(f => f.status === 'ARCHIVED');
  // Drafts (and anything Meta adds later) used to fall through both groups and vanish.
  const other = forms.filter(f => f.status !== 'ACTIVE' && f.status !== 'ARCHIVED');

  return (
    <>
      <Topbar
        title="Lead Forms"
        breadcrumb="Facebook Ads / Lead Forms"
        description="Forms attached to your Facebook Page for Meta Lead Ads"
        right={
          <div className="flex gap-2">
            <Button variant="secondary" iconLeft={<RefreshCw size={14} />} onClick={load}>
              Refresh
            </Button>
            <Button iconLeft={<Plus size={14} />} onClick={() => nav('/facebook/lead-forms/new')}>
              New form
            </Button>
          </div>
        }
      />

      <div className="mx-auto w-full max-w-[1100px] px-7 py-7 space-y-5">
        {pages.length > 1 && <label className="flex items-center gap-3 text-sm text-text-secondary">Facebook Page
          <select aria-label="Facebook Page" value={pageId} onChange={event => setPageId(event.target.value)} className="rounded-lg border border-border bg-surface px-3 py-2 text-text-primary">
            {pages.map(id => <option key={id} value={id}>Page {id}</option>)}
          </select>
        </label>}
        {pageId && <LeadPageSubscriptionCard key={pageId} pageId={pageId} />}
        {loading ? (
          <div className="space-y-3">
            <div className="skeleton h-24 rounded-2xl" />
            <div className="skeleton h-24 rounded-2xl" />
          </div>
        ) : loadError ? (
          <LoadErrorState what="your lead forms" onRetry={load} />
        ) : forms.length === 0 ? (
          <Card>
            <div className="py-12 text-center">
              <ClipboardList size={32} className="mx-auto mb-3 text-text-muted opacity-40" />
              <p className="text-sm font-semibold text-text-primary">No lead forms yet</p>
              <p className="mx-auto mt-1 max-w-sm text-xs text-text-muted">
                Create your first form to capture leads directly from Meta Lead Ads — submissions land
                in SlightorCRM after connecting lead delivery above.
              </p>
              <Button className="mt-4" iconLeft={<Plus size={14} />} onClick={() => nav('/facebook/lead-forms/new')}>
                Create a form
              </Button>
            </div>
          </Card>
        ) : (
          <>
            {active.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">Active ({active.length})</h3>
                <div className="grid gap-3 md:grid-cols-2">
                  {active.map(f => (
                    <FormCard key={f.id} form={f} onOpen={() => nav(`/facebook/lead-forms/${f.id}`)} onArchive={() => archive(f)} archiving={archivingId === f.id} />
                  ))}
                </div>
              </div>
            )}
            {other.length > 0 && (
              <div className={clsx('space-y-2', active.length > 0 && 'pt-4')}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">Other statuses ({other.length})</h3>
                <div className="grid gap-3 md:grid-cols-2">
                  {other.map(f => (
                    <FormCard key={f.id} form={f} onOpen={() => nav(`/facebook/lead-forms/${f.id}`)} badge={statusLabel(f.status)} archiving={false} />
                  ))}
                </div>
              </div>
            )}
            {archived.length > 0 && (
              <div className={clsx('space-y-2', (active.length > 0 || other.length > 0) && 'pt-4')}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">Archived ({archived.length})</h3>
                <div className="grid gap-3 md:grid-cols-2">
                  {archived.map(f => (
                    <FormCard key={f.id} form={f} onOpen={() => nav(`/facebook/lead-forms/${f.id}`)} archived archiving={false} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

interface FormCardProps {
  form: LeadGenFormSummary;
  onOpen: () => void;
  onArchive?: () => void;
  archived?: boolean;
  archiving: boolean;
  /** Status pill for forms outside the Active / Archived groups. */
  badge?: string;
}

function FormCard({ form, onOpen, onArchive, archived, archiving, badge }: FormCardProps) {
  return (
    <Card
      className={clsx('group hover:shadow-soft transition-shadow', archived && 'opacity-70')}
    >
      <div className="flex items-start justify-between gap-3 p-1">
        <button onClick={onOpen} className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2.5 mb-2">
            <div className={clsx(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
              archived ? 'bg-surface-alt text-text-muted' : 'bg-primary-soft text-primary',
            )}>
              <ClipboardList size={14} />
            </div>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <p className="truncate text-sm font-semibold text-text-primary">{form.name}</p>
                {badge && <span className="shrink-0 rounded-full bg-warning-soft px-2 py-0.5 text-2xs font-semibold text-warning">{badge}</span>}
              </div>
              <p className="truncate font-mono text-2xs text-text-muted">{form.id}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-muted">
            <span className="inline-flex items-center gap-1">
              <Users size={11} />
              <strong className="tabular-nums text-text-primary">{form.leads_count ?? 0}</strong> leads
            </span>
            {form.locale && <span className="uppercase font-mono">{form.locale}</span>}
            <span>{relativeTime(form.created_time)}</span>
          </div>
        </button>
        {!archived && onArchive && (
          <Button
            size="sm"
            variant="ghost"
            iconLeft={<Archive size={12} />}
            loading={archiving}
            onClick={(e) => { e.stopPropagation(); onArchive(); }}
          >
            Archive
          </Button>
        )}
      </div>
    </Card>
  );
}
